import React, { useState } from "react";
import {
  Container,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Paper,
  Box,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import StarIcon from "@mui/icons-material/Star";
import PetsIcon from "@mui/icons-material/Pets";
import SecurityIcon from "@mui/icons-material/Security";
import EmailIcon from "@mui/icons-material/Email";
import ChatIcon from "@mui/icons-material/Chat";

const CATEGORIES = [
  {
    icon: <RocketLaunchIcon sx={{ fontSize: 64, color: "#178B8E" }} />,
    title: "Getting Started",
    description: "New to sitting? Start here",
  },
  {
    icon: <AssignmentIcon sx={{ fontSize: 64, color: "#178B8E" }} />,
    title: "Managing Bookings",
    description: "Accept requests and manage bookings",
  },
  {
    icon: <AttachMoneyIcon sx={{ fontSize: 64, color: "#178B8E" }} />,
    title: "Earnings & Payouts",
    description: "Pricing, payouts, and taxes",
  },
  {
    icon: <StarIcon sx={{ fontSize: 64, color: "#178B8E" }} />,
    title: "Reviews & Ratings",
    description: "Build your reputation",
  },
  {
    icon: <PetsIcon sx={{ fontSize: 64, color: "#178B8E" }} />,
    title: "Pet Care Tips",
    description: "Best practices and safety",
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 64, color: "#178B8E" }} />,
    title: "Account & Safety",
    description: "Settings, privacy, and security",
  },
];

const FAQS = [
  {
    id: "faq1",
    question: "How do I get more booking requests?",
    answer:
      "Complete your profile with a professional photo and detailed bio. Set competitive rates for your area. Keep your calendar updated. Respond quickly to requests. Maintain a high rating by providing excellent service. Verified sitters get priority in search results.",
  },
  {
    id: "faq2",
    question: "How do I set my prices?",
    answer:
      "Go to your profile settings and navigate to 'Services & Pricing'. You can set different rates for boarding, house sitting, drop-in visits, and dog walking. Check what other sitters in your area charge to stay competitive.",
  },
  {
    id: "faq3",
    question: "When and how do I get paid?",
    answer:
      "Payment is released 24 hours after the booking ends, giving clients time to report any issues. Funds are deposited to your bank account within 2-3 business days. You can view all transactions in the Earnings section.",
  },
  {
    id: "faq4",
    question: "How do I update my availability?",
    answer:
      "Go to your Calendar and click dates to block them off. You can block single days or date ranges. Always keep your calendar current to avoid double bookings and ensure you only receive requests when you're available.",
  },
  {
    id: "faq5",
    question: "What if a client cancels?",
    answer:
      "If a client cancels more than 24 hours before the booking, you won't be paid. If they cancel within 24 hours, you'll receive 50% of the booking amount. Last-minute cancellations still pay you the full amount.",
  },
  {
    id: "faq6",
    question: "How do reviews work?",
    answer:
      "After each completed booking, both you and the client can leave reviews. Reviews are public and affect your rating. Aim for 5-star service! You can respond to reviews within 30 days.",
  },
  {
    id: "faq7",
    question: "What should I do in an emergency?",
    answer:
      "For immediate pet health emergencies, contact the nearest veterinarian and call the client immediately. You have access to their emergency contact. Document everything and report through the app. HappyPaws provides limited emergency coverage.",
  },
  {
    id: "faq8",
    question: "Can I decline a booking request?",
    answer:
      "Yes, but try to maintain a high acceptance rate (above 80%). Valid reasons include: dates not actually available, pet requirements outside your comfort zone, or concerns about the client. Be professional when declining.",
  },
  {
    id: "faq9",
    question: "How do I handle difficult pets?",
    answer:
      "Always meet pets before confirming bookings. Ask detailed questions about behavior. Set clear expectations with clients. For aggressive behavior, prioritize your safety and contact the client immediately. You can end a booking early if you feel unsafe.",
  },
  {
    id: "faq10",
    question: "What fees does HappyPaws charge?",
    answer:
      "HappyPaws takes a 15% service fee from each booking. This covers payment processing, insurance, customer support, and platform maintenance. Fees are automatically deducted before payouts.",
  },
  {
    id: "faq11",
    question: "How do I become a verified sitter?",
    answer:
      "Complete your profile 100%, upload a government ID, pass a background check, and complete the sitter training course. Verified sitters get a badge, priority in search results, and access to premium features.",
  },
  {
    id: "faq12",
    question: "Can I sit full-time or just occasionally?",
    answer:
      "Both! You control your availability and how many bookings you accept. Many sitters do this as a side income, while others build full-time businesses. Set your schedule to fit your lifestyle.",
  },
];

import SitterHeader from '../SitterHeader/SitterHeader';

const SitterHelpCenter: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedFaq, setExpandedFaq] = useState<string | false>(false);
  const [chatSnackbar, setChatSnackbar] = useState(false);

  const handleFaqChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedFaq(isExpanded ? panel : false);
    };

  const handleCategoryClick = (_categoryTitle: string) => {
    document.getElementById("faq-section")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Box sx={{ backgroundColor: '#FAF8F5', minHeight: '100vh' }}>
      <SitterHeader />
      <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Page Title */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: "text.primary" }}>
        Help Center
      </Typography>

      {/* Hero — Search */}
      <Box sx={{ textAlign: "center", maxWidth: 700, mx: "auto", mb: 6 }}>
        <Typography variant="h5" fontWeight={600} mb={1}>
          How can we help you?
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          Search for answers or browse categories below
        </Typography>
        <TextField
          fullWidth
          placeholder="Search for help..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            backgroundColor: "white",
            borderRadius: 3,
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              "&:hover fieldset": { borderColor: "#178B8E" },
              "&.Mui-focused fieldset": { borderColor: "#178B8E" },
            },
          }}
        />
      </Box>

      {/* Categories */}
      <Typography variant="h5" fontWeight={600} mb={3}>
        Browse by Category
      </Typography>
      <Grid container spacing={3} sx={{ mb: 6 }}>
        {CATEGORIES.map((category) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={category.title}>
            <Paper
              onClick={() => handleCategoryClick(category.title)}
              sx={{
                p: 3,
                borderRadius: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.25s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 4,
                },
              }}
            >
              <Box mb={1.5}>{category.icon}</Box>
              <Typography variant="h6" fontWeight={600} mb={0.5}>
                {category.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {category.description}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* FAQ */}
      <Box id="faq-section" sx={{ mb: 6 }}>
        <Typography variant="h5" fontWeight={600} mb={3}>
          Frequently Asked Questions
        </Typography>
        <Paper sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid #E0E0E0" }}>
          {FAQS.map((faq, index) => (
            <Accordion
              key={faq.id}
              expanded={expandedFaq === faq.id}
              onChange={handleFaqChange(faq.id)}
              disableGutters
              elevation={0}
              sx={{
                borderBottom: index < FAQS.length - 1 ? "1px solid #E0E0E0" : "none",
                "&:before": { display: "none" },
                "&.Mui-expanded": { backgroundColor: "rgba(23, 139, 142, 0.04)" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#178B8E" }} />}
                sx={{
                  px: 3,
                  py: 0.5,
                  "&.Mui-expanded": { borderBottom: "1px solid rgba(23, 139, 142, 0.2)" },
                }}
              >
                <Typography variant="subtitle1" fontWeight={500}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, py: 2 }}>
                <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      </Box>

      {/* Contact Support */}
      <Paper
        sx={{
          p: { xs: 4, md: 6 },
          borderRadius: 2,
          textAlign: "center",
          background: "linear-gradient(135deg, #E0F2F1 0%, #B2DFDB 100%)",
          boxShadow: "none",
        }}
      >
        <Typography variant="h5" fontWeight={700} mb={1}>
          Still need help?
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          Our support team is here for you
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="center"
          alignItems="center"
          mb={2}
        >
          <Button
            variant="contained"
            size="large"
            startIcon={<EmailIcon />}
            href="mailto:support@happypaws.com"
            sx={{
              textTransform: "none",
              backgroundColor: "#178B8E",
              "&:hover": { backgroundColor: "#146f72" },
              px: 3,
            }}
          >
            Email Support
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<ChatIcon />}
            onClick={() => setChatSnackbar(true)}
            sx={{
              textTransform: "none",
              borderColor: "#178B8E",
              color: "#178B8E",
              "&:hover": { borderColor: "#146f72", backgroundColor: "rgba(23,139,142,0.05)" },
              px: 3,
            }}
          >
            Chat with Us
          </Button>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          Email: support@happypaws.com
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Response time: Within 24 hours
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sitter Support Hours: Mon–Fri 9am–6pm EST
        </Typography>
      </Paper>

      <Snackbar
        open={chatSnackbar}
        autoHideDuration={3000}
        onClose={() => setChatSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="info" onClose={() => setChatSnackbar(false)}>
          Live chat is coming soon!
        </Alert>
      </Snackbar>
    </Container>
    </Box>
  );
};

export default SitterHelpCenter;
